import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { EntityList, PageBackground } from '../shared';
import { useGroupsDataContext } from '../../contexts/GroupsDataContext';
import { Group } from '../../types/groups';
import { 
  useGroupsFilters,
  useGroupsTableConfig, 
} from './hooks';

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [imageStates, setImageStates] = useState<Record<string, { loaded: boolean; error: boolean }>>({});

  // Get groups data from context
  const { data, loading, error, loadingMore } = useGroupsDataContext();
  const columns = useGroupsTableConfig();
  const { 
    setAppliedFilters, 
    filterDefinitions, 
    filteredData,
  } = useGroupsFilters(data, searchQuery);

  // Handlers
  const handleCreateGroup = useCallback(() => {
    console.log('Create group');
  }, []);

  const handleDownloadCSV = useCallback(() => {
    console.log('Download CSV');
  }, []);

  const handlePrint = useCallback(() => {
    console.log('Print');
  }, []);

  const handleRowClick = useCallback((group: any) => {
    navigate(`/groups/detail/${group.id}`);
  }, [navigate]);

  // Gallery tile renderer for group cards
  const renderGroupGalleryTile = useCallback((group: Group) => {
    const IconComponent = (LucideIcons as any)[group.typeIcon];
    const imageState = imageStates[group.id] || { loaded: false, error: false };
    const shouldShowImage = group.avatarUrl && !imageState.error;

    const handleImageLoad = () => {
      setImageStates(prev => ({
        ...prev,
        [group.id]: { loaded: true, error: false }
      }));
    };

    const handleImageError = () => {
      setImageStates(prev => ({
        ...prev,
        [group.id]: { loaded: false, error: true }
      }));
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col">
        {/* Image/Icon Header - Top half of card */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}> {/* 16:9 aspect ratio */}
          {shouldShowImage ? (
            <>
              {/* Skeleton loader - shown while image is loading */}
              {!imageState.loaded && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              )}
              
              {/* Actual image - fades in when loaded */}
              <img
                src={group.avatarUrl}
                alt={group.name}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  imageState.loaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {IconComponent && <IconComponent size={64} />}
            </div>
          )}
        </div>
        
        {/* Content - Bottom half of card */}
        <div className="p-4 flex-1 flex flex-col">

          {/* Name */}
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2 line-clamp-2 flex-shrink-0">
            {group.name}
          </h3>

          {/* Info - Fixed height container */}
          <div className="space-y-2 flex-shrink-0" style={{ minHeight: '96px' }}>
            {/* Type and Department */}
            <div className="flex flex-col gap-1">
              {group.type && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                    {group.type}
                  </span>
                </div>
              )}
              {group.department && (
                <div className="text-xs text-gray-500 text-center">
                  {group.department}
                </div>
              )}
            </div>

            {/* Leader */}
            {group.leader && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <LucideIcons.User size={14} className="flex-shrink-0 text-gray-400" />
                <span className="truncate">{group.leader}</span>
              </div>
            )}

            {/* Member Count and Status */}
            <div className="flex items-center justify-center gap-3 text-sm">
              <div className="flex items-center gap-1 text-gray-600">
                <LucideIcons.Users size={14} className="text-gray-400" />
                <span>{group.memberCount}</span>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                group.isFull 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {group.isFull ? 'Full' : 'Open'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }, [imageStates]);

  // Show loading state with page structure
  if (loading) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-4 sm:mb-8">
            <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          
          {/* Search bar skeleton */}
          <div className="h-10 bg-gray-200 rounded w-full mb-4 animate-pulse"></div>
          
          {/* Gallery grid skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
                <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </PageBackground>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center max-w-md">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <LucideIcons.AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Groups</h3>
                <p className="text-red-700 mb-4">{error.message}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }

  return (
    <EntityList<Group>
      // Page configuration
      objectTitle="Groups"
      
      // Primary action
      primaryAction={{
        label: 'Create Group',
        icon: LucideIcons.Plus,
        onClick: handleCreateGroup
      }}
      
      // Search
      searchPlaceholder="Search by group name or leader..."
      onSearchChange={setSearchQuery}
      
      // View configuration
      enableGalleryView={true}
      defaultView="table"
      
      // Table configuration
      columns={columns}
      getRowKey={(group) => group.id}
      emptyMessage="No groups found"
      pageSize={10}
      onRowClick={handleRowClick}
      
      // Gallery configuration
      renderGalleryTile={renderGroupGalleryTile}
      galleryPageSize={12}
      
      // Data
      filteredData={filteredData}
      
      // Filters
      filterDefinitions={filterDefinitions}
      onApplyFilters={setAppliedFilters}
      
      // Default actions
      onDownloadCSV={handleDownloadCSV}
      onPrint={handlePrint}
      
      // Loading state
      loadingMore={loadingMore}
    />
  );
};

export default Groups;